import React, { memo } from 'react';
import { Checkbox } from '@myob/myob-widgets';
import './TermsOfUse.scss';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { FEELIX_THEMES, LEGAL_LINKS, SOLO_PRODUCT_IDS, SUBSCRIPTION_TYPE } from '../../constants';

type TermsOfUseProps = {
  isTermsCheck: boolean;
  termsErrorMessage: string;
  setTerms: () => void;
  sendPromotionsCheck:boolean;
  setSendPromotions: () => void;
}

const getTermsOfUseUrl = (productId: string, region: string): string => {
  const links = region.toUpperCase() === 'NZ' ? LEGAL_LINKS.NZ : LEGAL_LINKS.AU;
  if (SOLO_PRODUCT_IDS.includes(productId)) {
    return links.SOLO_TERMS_OF_USE;
  }
  return links.MYOB_BUSINESS_TERMS_OF_USE;
};

const Link = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a
    target='_blank'
    rel='noopener noreferrer'
    href={href}
  >
    {children}
  </a>
);

const offerTermsAndConditionsLink = (href: string) =>
  <Link href={href}>
    {' '}Offer Terms and Conditions
  </Link>;

const getTermsAndConditions = ({
  productId,
  region,
  theme,
  promoCode,
  promotionTermsLink
}: {
  productId: string,
  region: string,
  theme: string,
  promoCode?: string,
  promotionTermsLink?: string
}) => {
  const REGIONAL_LINKS = region.toUpperCase() === 'NZ' ? LEGAL_LINKS.NZ : LEGAL_LINKS.AU;

  if (theme === FEELIX_THEMES.PARTNER) {
    return (
      <div className='terms-of-use'>
        <span data-testid='t&c'>
          I&apos;ve read and agreed to
          the <Link href={REGIONAL_LINKS.MYOB_PARTNER_PROGRAM_TERMS}>Partner Program Terms and Conditions</Link>,
          the <Link href={REGIONAL_LINKS.MYOB_PRACTICE_TERMS_OF_USE}>MYOB Practice Terms of Use</Link>,
          the <Link href={REGIONAL_LINKS.MYOB_BUSINESS_TERMS_OF_USE}>MYOB Business Terms of Use</Link>{' '}
          and the <Link href={REGIONAL_LINKS.MYOB_SALES_SERVICES_AGREEMENT}>Sales and Services Agreement</Link>{' '}
          and I acknowledge that I have read
          the <Link href={REGIONAL_LINKS.PRIVACY_STATEMENT}>Privacy Policy</Link>{' '}
          and the <Link href={REGIONAL_LINKS.PRIVACY_COLLECTION_NOTICE}>Privacy Collection Notice</Link>.
          By joining the program I consent to receive marketing communications from MYOB including promotional offers.
        </span>
      </div>
    );
  }
  const showOfferTerms = promoCode && promotionTermsLink

  const links = [<Link key='tc1' href={getTermsOfUseUrl(productId, region)}>Terms of Use</Link>];
  if (showOfferTerms) {
    links.push(<React.Fragment key='tc2'>, the {offerTermsAndConditionsLink(promotionTermsLink)}</React.Fragment>);
  }
  links.push(
    <React.Fragment
      key='tc3'
    >
      , <Link href={REGIONAL_LINKS.PRIVACY_STATEMENT}>Privacy Policy</Link>
    </React.Fragment>
  );
  links.push(
    <React.Fragment
      key='tc4'
    > and <Link href={REGIONAL_LINKS.PRIVACY_COLLECTION_NOTICE}>Privacy Collection Notice.</Link>
    </React.Fragment>
  );
  return (
    <div className={'terms-of-use'}>
      <span data-testid={'t&c'}>
        I&apos;ve read and agreed to the{' '}
        {links}
      </span>
    </div>
  );
}

const TermsOfUse: React.FC<TermsOfUseProps> = ({
  isTermsCheck,
  termsErrorMessage,
  setTerms,
  sendPromotionsCheck,
  setSendPromotions
}) => {
  const {
    region,
    productId,
    promoCode,
    promotionTermsLink,
    subscriptionType,
    theme
  } = useSessionContext();
  if (subscriptionType === SUBSCRIPTION_TYPE.TRIAL) {
    return null;
  }
  const hidePromotionsCheckbox = theme === FEELIX_THEMES.PARTNER;
  return (
    <div>
      <Checkbox
        className={'terms-of-use-checkbox'}
        data-testid='terms-of-use'
        name='terms-of-use'
        label={getTermsAndConditions({ productId, region, theme, promoCode, promotionTermsLink })}
        onChange={() => setTerms()}
        checked={isTermsCheck}
        errorMessage={termsErrorMessage}
      />

      {!hidePromotionsCheckbox && (
        <Checkbox
          className={'send-promotion-checkbox'}
          data-testid='send-promotion'
          name='send-promotion'
          label={<div className={'do-not-send-promotion'}>
            <span>
              Keep me in the loop with the latest news and special offers.
            </span>
          </div>}
          onChange={() => setSendPromotions()}
          checked={sendPromotionsCheck}
        />
      )}
    </div>
  );
};
export default memo(TermsOfUse);
